import { useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import AppIcon from "../../../components/AppIcon";
import { Spinner } from "../../../components/ui/Spinner";
import { getApiErrorMessage } from "../../../utils/apiErrors";
import {
  PLATFORM_REVOKE_PHRASE,
  SessionRevocationResult,
  revokeCompanySessions,
  revokePlatformSessions,
} from "../../../api/General/SessionRevocation";

/**
 * CO-708 — "Sign out all users" danger zone. Operator-only surfaces (both host routes are
 * OPERATOR_ROLES, and the API is Policy = "Operator").
 *
 * The typed confirmation is enforced by the SERVER; the disabled button here only saves a round trip.
 * The phrase/company name is compared trimmed and case-sensitive on both sides so the UI never
 * enables a submit the server will refuse.
 */
type Props =
  | { scope: "company"; companyId: number; companyName: string }
  | { scope: "platform" };

const REASON_MAX = 500;

export default function SessionRevocationPanel(props: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SessionRevocationResult | null>(null);

  const isCompany = props.scope === "company";
  const expected = isCompany ? props.companyName.trim() : PLATFORM_REVOKE_PHRASE;
  const canSubmit =
    !busy && expected.length > 0 && confirm.trim() === expected && reason.trim().length > 0 && reason.trim().length <= REASON_MAX;

  const reset = () => {
    setOpen(false);
    setReason("");
    setConfirm("");
    setError(null);
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const input = { confirm: confirm.trim(), reason: reason.trim() };
      const res = isCompany
        ? await revokeCompanySessions(props.companyId, input)
        : await revokePlatformSessions(input);
      if (res?.status === 200 && res.payload) {
        setResult(res.payload);
        reset();
      } else {
        setError(getApiErrorMessage(res, t, t("sessionRevocation.failed"), " "));
      }
    } catch (err) {
      setError(getApiErrorMessage(err, t, t("sessionRevocation.failed"), " "));
    } finally {
      setBusy(false);
    }
  };

  const title = isCompany ? t("sessionRevocation.companyTitle") : t("sessionRevocation.platformTitle");
  const description = isCompany
    ? t("sessionRevocation.companyDescription", { company: props.companyName })
    : t("sessionRevocation.platformDescription");

  return (
    <section
      aria-labelledby={`session-revocation-${props.scope}-title`}
      className="w-full rounded-lg border border-destructive/40 bg-destructive/5 p-4"
      data-testid={`session-revocation-${props.scope}`}
    >
      <div className="flex items-start gap-3">
        <AppIcon name="ShieldAlert" size={20} className="mt-0.5 shrink-0 text-destructive" />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-destructive">{t("sessionRevocation.dangerZone")}</p>
          <h2 id={`session-revocation-${props.scope}-title`} className="text-base font-semibold text-foreground">
            {title}
          </h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>

      {result && (
        <div
          role="status"
          className="mt-4 rounded-md border border-emerald-500/50 bg-emerald-500/10 p-3 text-sm text-emerald-700 dark:text-emerald-400"
          data-testid="session-revocation-result"
        >
          {t("sessionRevocation.result", {
            users: result.users,
            tokens: result.tokens,
            authorizations: result.authorizations,
          })}
        </div>
      )}

      {!open ? (
        <div className="mt-4">
          <Button variant="destructive" onClick={() => { setResult(null); setOpen(true); }}>
            {title}
          </Button>
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-3">
          <Input
            required
            label={t("sessionRevocation.reasonLabel")}
            description={t("sessionRevocation.reasonHelp")}
            value={reason}
            maxLength={REASON_MAX}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
          />
          <Input
            required
            autoComplete="off"
            spellCheck={false}
            dir="auto"
            label={
              isCompany
                ? t("sessionRevocation.confirmCompanyLabel", { company: props.companyName })
                : t("sessionRevocation.confirmPlatformLabel", { phrase: PLATFORM_REVOKE_PHRASE })
            }
            value={confirm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirm(e.target.value)}
          />
          {error && (
            <div role="alert" className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button variant="destructive" disabled={!canSubmit} onClick={submit}>
              {busy ? <Spinner size="sm" /> : t("sessionRevocation.submit")}
            </Button>
            <Button variant="outline" disabled={busy} onClick={reset}>
              {t("Cancel")}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
